import React, { useEffect, useState } from "react";
import DateSelect from "../common/DateSelect";
import {
  Typography,
  CircularProgress,
  Table,
  TableRow,
  TableCell,
  TableHead,
  TableBody,
  Link,
  Collapse,
  IconButton,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useFormikContext } from "formik";
import { RecordWithEnumeratedIndex } from "../client/types";
import useFetchInvestedProjects from "./hooks/useFetchMetric";
import {
  CardHeading,
  CardWrapper,
  ElementWrapper,
  Error,
} from "./common-components";
import { Dates } from "../common/types";
import { formatNumber } from "./formatNumber";
import routes from "../../routes";

export interface IndividualInvestment {
  clientId: string;
  clientName: string;
  totalInvested: string;
}

export interface SolutionInvestment {
  solutionId: string;
  name: string;
  value: number;
  unit: string;
  investmentsPerClient?: Array<IndividualInvestment>;
}

const ProjectsInvestedCardContent = () => {
  const id = "projectInvestments";
  const { values, isValid } =
    useFormikContext<RecordWithEnumeratedIndex<Dates<typeof id>, number>>();
  const { loading, error, fetchMetric, metric } = useFetchInvestedProjects<{
    solutionInvestments?: Array<SolutionInvestment>;
  }>("invested projects");
  const [openRows, setOpenRows] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (isValid) {
      fetchMetric({
        fromDate: values[`${id}-start-date`],
        toDate: values[`${id}-end-date`],
      });
    }
  }, [values, fetchMetric, id, isValid]);

  const toggleDropdown = (solutionId: string) => {
    setOpenRows((prev) => ({ ...prev, [solutionId]: !prev[solutionId] }));
  };

  const element = () => {
    if (loading) {
      return <CircularProgress />;
    }

    if (error || !metric) {
      return <Error />;
    }

    if (
      !metric?.solutionInvestments ||
      metric?.solutionInvestments?.length === 0
    ) {
      return (
        <Typography sx={{ fontSize: 16, margin: 4 }}>
          No investment data found between selected dates
        </Typography>
      );
    }

    return (
      <Table>
        <TableHead>
          <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
            <TableCell />
            <TableCell>
              <Typography sx={{ fontSize: 18, fontWeight: "bold" }}>
                Project
              </Typography>
            </TableCell>
            <TableCell>
              <Typography sx={{ fontSize: 18, fontWeight: "bold" }}>
                Total
              </Typography>
            </TableCell>
            <TableCell>
              <Typography sx={{ fontSize: 18, fontWeight: "bold" }}>
                Unit
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {metric.solutionInvestments.map(
            ({ solutionId, name, value, unit, investmentsPerClient }) => (
              <React.Fragment key={solutionId}>
                <TableRow>
                  <TableCell>
                    {investmentsPerClient &&
                      investmentsPerClient.length > 0 && (
                        <IconButton
                          aria-label="expand row"
                          size="small"
                          onClick={() => toggleDropdown(solutionId)}
                        >
                          {openRows[solutionId] ? (
                            <KeyboardArrowUpIcon />
                          ) : (
                            <KeyboardArrowDownIcon />
                          )}
                        </IconButton>
                      )}
                  </TableCell>
                  <TableCell>
                    <Link
                      href={routes.solutions.view({ id: solutionId })}
                      underline="none"
                      target="_blank"
                      rel="noreferrer"
                      sx={{
                        fontSize: 16,
                        marginTop: 2,
                        overflowWrap: "break-word",
                        wordWrap: "break-word",
                        hyphens: "auto",
                        maxWidth: 100,
                      }}
                    >
                      {name}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: 16 }}>
                      {formatNumber(value)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      sx={{
                        fontSize: 16,
                        maxWidth: 100,
                        overflowWrap: "break-word",
                        wordWrap: "break-word",
                        hyphens: "auto",
                      }}
                    >
                      {unit}
                    </Typography>
                  </TableCell>
                </TableRow>

                {investmentsPerClient && investmentsPerClient.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ padding: 0 }}>
                      <Collapse
                        in={openRows[solutionId]}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Table sx={{ marginLeft: 4, marginBottom: 2 }}>
                          <TableHead>
                            <TableRow>
                              <TableCell>
                                <Typography
                                  sx={{ fontSize: 16, fontWeight: "bold" }}
                                >
                                  Client name
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  sx={{ fontSize: 16, fontWeight: "bold" }}
                                >
                                  Total invested
                                </Typography>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {investmentsPerClient?.map((investment, index) => (
                              <TableRow key={`${investment.clientId}-${index}`}>
                                <TableCell>
                                  <Link
                                    href={routes.clients.view({
                                      id: investment.clientId,
                                    })}
                                    underline="none"
                                    target="_blank"
                                    rel="noreferrer"
                                    sx={{
                                      fontSize: 16,
                                      marginTop: 2,
                                      overflowWrap: "break-word",
                                      wordWrap: "break-word",
                                      hyphens: "auto",
                                      maxWidth: 100,
                                    }}
                                  >
                                    {investment.clientName}
                                  </Link>
                                </TableCell>

                                <TableCell>
                                  <Typography sx={{ fontSize: 16 }}>
                                    {formatNumber(investment.totalInvested)}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ),
          )}
        </TableBody>
      </Table>
    );
  };

  return (
    <>
      <CardHeading heading="Total sales per project" />
      <DateSelect
        dateName="projectInvestments"
        minDate={new Date("2020-01-01")}
      />
      <ElementWrapper>{element()}</ElementWrapper>
    </>
  );
};

interface ProjectsInvestmentCardWrapperProps {
  children: React.ReactNode;
}

const ProjectsInvestmentCardWrapper: React.FC<
  ProjectsInvestmentCardWrapperProps
> = ({ children }) => (
  <CardWrapper id="projectInvestments">{children}</CardWrapper>
);

export const ProjectsInvestedCard = () => (
  <ProjectsInvestmentCardWrapper>
    <ProjectsInvestedCardContent />
  </ProjectsInvestmentCardWrapper>
);
